// Lib Imports
import React, { useMemo, useCallback, useState } from 'react';
import { Box, Text, TextInput } from 'grommet';
import { Close } from 'grommet-icons';
import { useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

// Core Imports
import FormField from 'granite-admin/core/components/FormField';

// Application Imports
import ImageCrop from './ImageCrop';
import { FileList, FileSelectedPreview, ImagePreview, UploadZone, VideoPreview, formatBytes } from './utils';
const UploadComponent = ({
  accept,
  multiple,
  maxSize,
  imageCropAspect,
  fixedAspect,
  maxFiles,
  minSize,
  fileLimit,
  disabled,
  noClick,
  noDrag,
  className
}) => {
  const {
    values,
    setFieldValue,
    touched,
    errors,
    handleBlur,
    handleChange,
    isSubmitting
  } = useFormikContext();
  const [doc, setDoc] = useState(null);
  const isPDF = files => {
    return files.path.slice(files.path.lastIndexOf('.') + 1)?.toLowerCase() === 'pdf';
  };
  const setDocUrl = file => {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function () {
      setDoc(reader.result);
    };
  };
  // Define array for initial values
  const onDrop = useCallback(acceptedFiles => {
    let array = [];
    if (acceptedFiles.length > 0) {
      array = [...acceptedFiles, ...values.files];
    }
    if (array.length === 1 && isPDF(array[0])) setDocUrl(array[0]);else if (doc) setDoc(null);
    setFieldValue('files', array);
    let name = array?.[0]?.name ?? '';
    setFieldValue('file_name', array.length === 1 ? name.slice(0, name.lastIndexOf('.')) : '');
    setFieldValue('ext', array.length === 1 ? name.slice(name.lastIndexOf('.') + 1)?.toLowerCase() : '');
  }, [setFieldValue, values.files, doc]);
  const {
    getRootProps,
    getInputProps,
    fileRejections
  } = useDropzone({
    multiple,
    accept,
    maxSize,
    onDrop,
    maxFiles,
    minSize,
    disabled,
    noClick,
    noDrag
  });

  // Define file rejections in string for error
  const validationError = useMemo(() => {
    let exArray = fileRejections.map(ex => {
      let errMsgArr = ex.errors.map(err => {
        return err.message;
      });
      let errMsgStr = errMsgArr.join(', ');
      return `The file "${ex.file.name}" cannot be uploaded as ${errMsgStr}`;
    });
    return exArray;
  }, [fileRejections]);
  const removeFile = (files, selectedFile, setFieldValue) => {
    let fileArr = [...files];
    fileArr = fileArr.filter(i => i !== selectedFile);
    if (fileArr.length === 1 && isPDF(fileArr[0])) setDocUrl(fileArr[0]);else if (doc) setDoc(null);
    setFieldValue('files', fileArr);
    if (fileArr.length <= 1) {
      let name = fileArr?.[0]?.name ?? '';
      setFieldValue('file_name', fileArr.length === 1 ? name.slice(0, name.lastIndexOf('.')) : '');
      setFieldValue('ext', fileArr.length === 1 ? name?.slice(name.lastIndexOf('.') + 1) : '');
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, validationError.length > 0 && /*#__PURE__*/React.createElement(Box, {
    overflow: "auto",
    height: "60px",
    margin: {
      bottom: 'small'
    }
  }, validationError.map((text, index) => {
    return /*#__PURE__*/React.createElement(Text, {
      key: index,
      height: {
        min: '32px'
      },
      size: "10px",
      color: "red",
      margin: {
        bottom: 'small'
      }
    }, text.replace(`${maxSize} bytes`, `${formatBytes(maxSize)}.`));
  })), values?.files?.length < fileLimit && /*#__PURE__*/React.createElement(Box, {
    margin: {
      bottom: 'medium'
    }
  }, /*#__PURE__*/React.createElement(UploadZone, {
    getRootProps: getRootProps,
    getInputProps: getInputProps,
    className: className
  })), values.files.length === 1 ?
  /*#__PURE__*/
  // One file chosen, display editable title
  React.createElement(Box, {
    justify: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    basis: "full",
    direction: "row"
  }, /*#__PURE__*/React.createElement(FormField, {
    name: `file_name`,
    label: "File Name",
    error: touched.file_name && errors.file_name,
    width: "100%",
    noBorder: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    name: 'file_name',
    placeholder: "File Name",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.file_name,
    disabled: isSubmitting
  })), /*#__PURE__*/React.createElement(Box, {
    alignSelf: "center",
    justify: "center",
    pad: "small",
    onClick: () => removeFile(values.files, values.files[0], setFieldValue)
  }, /*#__PURE__*/React.createElement(Close, {
    size: "16px"
  }))), values.ext === 'jpg' || values.ext === 'jpeg' || values.ext === 'webp' || values.ext === 'png' ? imageCropAspect ? /*#__PURE__*/React.createElement(ImageCrop, {
    image: values.files[0],
    aspect: imageCropAspect,
    fixedAspect: fixedAspect,
    setFieldValue: setFieldValue,
    isSubmitting: isSubmitting
  }) :
  /*#__PURE__*/
  // Preview Image
  React.createElement(ImagePreview, {
    file: values.files[0]
  }) : values.ext === 'mp4' || values.ext === 'webm' ?
  /*#__PURE__*/
  // Preview Video
  React.createElement(VideoPreview, {
    file: values.files[0]
  }) : values.ext === 'pdf' ?
  /*#__PURE__*/
  // Preview PDF
  React.createElement(React.Fragment, null, doc && /*#__PURE__*/React.createElement("iframe", {
    title: "document-pdf",
    src: doc,
    width: "100%",
    height: "100%"
  })) :
  /*#__PURE__*/
  // File has been selected confirmation
  React.createElement(FileSelectedPreview, null)) : values.files.length > 1 ?
  /*#__PURE__*/
  // List of files (multiple only)
  React.createElement(FileList, {
    files: values.files,
    removeFile: removeFile,
    setFieldValue: setFieldValue
  }) : null);
};
UploadComponent.propTypes = {
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  imageCropAspect: PropTypes.number,
  fixedAspect: PropTypes.bool,
  maxSize: PropTypes.any,
  minSize: PropTypes.number,
  disabled: PropTypes.bool,
  noClick: PropTypes.bool,
  noDrag: PropTypes.bool,
  maxFiles: PropTypes.number,
  className: PropTypes.string,
  fileLimit: PropTypes.number
};
UploadComponent.defaultProps = {
  accept: '',
  title: 'Upload File',
  multiple: false,
  imageCropAspect: null,
  fixedAspect: false,
  maxSize: 1.048576e8,
  // 100 mB
  maxFiles: 0,
  minSize: 0,
  fileLimit: Infinity,
  noClick: false,
  noDrag: false,
  disabled: false
};
export default UploadComponent;